// The write-relay protocol between gTables (in Giraffe's iframe) and its own
// service popup — ported from giraffe-excel-poc's src/sync/popup.ts, which
// proved the mechanism live. See linkFsAccess.ts for why a popup is needed at
// all: Chrome blocks the File System Access pickers, and every write after
// them, inside a cross-origin iframe.
//
// A FIXED window name ("gtables-link-popup") is what makes "one window
// services multiple tabs" work for free — `window.open(url, name, ...)` on a
// name already open focuses the existing window instead of making a second
// one, in every browser this matters for.

export const LINK_POPUP_NAME = 'gtables-link-popup';

export const FOLDER_CHOSEN_MESSAGE = 'gtables-folder-chosen';
export const WRITE_REQUEST_MESSAGE = 'gtables-write-request';
export const WRITE_RESULT_MESSAGE = 'gtables-write-result';

export interface FolderChosenMessage {
  type: typeof FOLDER_CHOSEN_MESSAGE;
  workbookFileName: string;
}

export type WriteAction =
  | { kind: 'writeJson'; fileName: string; content: string }
  | { kind: 'readXlsx' };

export interface WriteRequestMessage {
  type: typeof WRITE_REQUEST_MESSAGE;
  requestId: string;
  action: WriteAction;
}

export interface WriteResultMessage {
  type: typeof WRITE_RESULT_MESSAGE;
  requestId: string;
  ok: boolean;
  error?: string;
  /** Present only for a successful `readXlsx` — the file's current bytes. */
  bytes?: ArrayBuffer;
}

/**
 * Open (or focus, if already open — see `LINK_POPUP_NAME`) the service popup.
 * `?popup=link` is how `main.tsx` tells the app to render `LinkPopupPage`
 * instead of the ordinary app on this load.
 */
export function openLinkPopup(): Window | null {
  const url = `${window.location.pathname}?popup=link`;
  return window.open(url, LINK_POPUP_NAME, 'width=480,height=360');
}

/**
 * Posts a write request to the still-open popup and resolves with its reply.
 * Rejects if the popup is missing/closed, or if no reply arrives within the
 * timeout (e.g. the popup navigated away).
 */
export function requestPopupWrite(popup: Window | null, action: WriteAction, timeoutMs = 15000): Promise<WriteResultMessage> {
  if (!popup || popup.closed) {
    return Promise.reject(new Error('The link window isn’t open. Click the link status to open it again.'));
  }
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Timed out waiting for the link window to respond.'));
    }, timeoutMs);

    function onMessage(event: MessageEvent<WriteResultMessage>): void {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== WRITE_RESULT_MESSAGE) return;
      if (event.data.requestId !== requestId) return;
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(event.data);
    }
    window.addEventListener('message', onMessage);

    const message: WriteRequestMessage = { type: WRITE_REQUEST_MESSAGE, requestId, action };
    popup.postMessage(message, window.location.origin);
  });
}

export function isPopupWindow(): boolean {
  return new URLSearchParams(window.location.search).get('popup') === 'link';
}
